import {Injectable, inject} from '@angular/core';
import {Observable, catchError, forkJoin, map, of} from 'rxjs';
import {DateTime} from 'luxon';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {EventCalendarService} from '../../services/event-calendar/event-calendar.service';
import {EventCalendarMonthService} from '../../services/event-calendar/event-calendar-month.service';
import {OccurrenceService} from '../../services/event-calendar/occurrence.service';
import {ServiceProviderHub} from '@generated-backend-types';
import {
  BERLIN_TZ,
  OCCURRENCES_RESOURCE_ID,
  OCCURRENCE_CATEGORY_PREFIX,
  RESERVED_OPTION_ORDER,
  toBerlinIso,
} from './event-calendar.constants';

import EventCalendarEntry = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Calendar.EventCalendarEntry;
import EventCalendarRow = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Calendar.EventCalendarRow;
import EventCalendarMonthRow = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Calendar.Month.EventCalendarMonthRow;
import ModelStateEnum = ServiceProviderHub.App.Enums.ModelStateEnum;
import OccurrenceCategoryEnum = ServiceProviderHub.App.Enums.Eventmanagement.Occurrence.OccurrenceCategoryEnum;
import EventmanagementCalendarEntryTypeEnum = ServiceProviderHub.App.Enums.Eventmanagement.Calendar.EventmanagementCalendarEntryTypeEnum;
import CalendarEntryBackgroundStyleEnum = ServiceProviderHub.App.Enums.Eventmanagement.Events.Calendar.CalendarEntryBackgroundStyleEnum;
import Occurrence = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Occurrence.Occurrence;

type CalendarRow = EventCalendarRow | EventCalendarMonthRow;
interface CalendarPage {
  rows: CalendarRow[];
  entries: EventCalendarEntry[];
}

export interface CalendarResource {
  id: string;
  title: string;
  parentId: string;
  order: number;
  secondaryOrder: number;
  isRoom: boolean;
  roomIsNotPublished: boolean;
}

export interface CalendarEvent {
  id: string;
  resourceId: string;
  entry: EventCalendarEntry;
  start: string | undefined;
  end: string | undefined;
}

export interface CalendarPayload {
  resources: CalendarResource[];
  events: CalendarEvent[];
}

@Injectable({providedIn: 'root'})
export class EventCalendarDataService {
  private calendar = inject(EventCalendarService);
  private calendarMonth = inject(EventCalendarMonthService);
  private occurrenceService = inject(OccurrenceService);
  private translate = inject(TranslateService);
  private toastr = inject(ToastrService);

  public load(start: DateTime, view: 'day' | 'week' | 'month'): Observable<CalendarPayload> {
    const startOfDay = start.startOf('day');
    const end = startOfDay.endOf(view);
    const timeframe = `${startOfDay.toUTC().toISO()},${end.toUTC().toISO()}`;
    const params = {'filter[relevant_timeframe]': timeframe};

    const handleCalendarError = (): Observable<CalendarPage> => {
      this.toastr.error(this.translate.instant('event_management.calendar.error_loading_calendar'));
      return of({rows: [], entries: []});
    };

    const calendar$: Observable<CalendarPage> = view === 'month'
      ? this.calendarMonth.getPage(1, params).pipe(
          map(res => ({
            rows: res.data[0]?.rows ?? [],
            entries: res.data[0]?.entries ?? [],
          })),
          catchError(handleCalendarError)
        )
      : this.calendar.getPage(1, params).pipe(
          map(res => ({
            rows: res.data[0]?.rows ?? [],
            entries: res.data[0]?.entries ?? [],
          })),
          catchError(handleCalendarError)
        );

    const occurrences$: Observable<Occurrence[]> = this.occurrenceService.getPage(1, {...params, per_page: '1000'}).pipe(
      map(res => res.data),
      catchError(() => {
        this.toastr.error(this.translate.instant('event_management.occurrences.error_loading'));
        return of([] as Occurrence[]);
      })
    );

    return forkJoin([calendar$, occurrences$]).pipe(
      map(([calendarPage, occurrences]) => this.compose(calendarPage, occurrences, view))
    );
  }

  private compose(calendarPage: CalendarPage, occurrences: Occurrence[], view: 'day' | 'week' | 'month'): CalendarPayload {
    const resources = this.mapResources(calendarPage.rows, occurrences);
    const events = [
      ...this.mapCalendarEntries(calendarPage.entries, view),
      ...this.mapOccurrences(occurrences, view),
    ];
    return {resources, events};
  }

  private mapResources(rows: CalendarRow[], occurrences: Occurrence[]): CalendarResource[] {
    const resources: CalendarResource[] = rows.map(row => {
      let title = row.parent_id === null ? row.title : this.translate.instant(`enums.eventResourceStateEnum.${row.title}`);
      const roomIsNotPublished = row.room_state === ModelStateEnum.ARCHIVED || row.room_state === ModelStateEnum.LOCKED || row.room_state === ModelStateEnum.DRAFT;
      if (roomIsNotPublished) {
        title += ` (${this.translate.instant(`enums.modelStateEnum.${row.room_state}`)})`;
      }
      return {
        id: row.id,
        title,
        parentId: row.parent_id ?? '',
        order: row.position,
        secondaryOrder: RESERVED_OPTION_ORDER[row.title] ?? 0,
        isRoom: row.parent_id === null,
        roomIsNotPublished,
      };
    });

    if (occurrences.length > 0) {
      resources.unshift({
        id: OCCURRENCES_RESOURCE_ID,
        title: this.translate.instant('authenticated.event_calendar.occurrences'),
        parentId: '',
        order: -1,
        secondaryOrder: 0,
        isRoom: true,
        roomIsNotPublished: false,
      });
      const categories = [...new Set(occurrences.map(o => o.category))]
        .filter(c => c !== OccurrenceCategoryEnum.PUBLIC_HOLIDAY);
      categories.forEach((category, index) => {
        resources.push({
          id: `${OCCURRENCE_CATEGORY_PREFIX}${category}`,
          title: this.translate.instant(`enums.occurrenceCategoryEnum.${category}`),
          parentId: OCCURRENCES_RESOURCE_ID,
          order: -1,
          secondaryOrder: index,
          isRoom: false,
          roomIsNotPublished: false,
        });
      });
    }
    return resources;
  }

  private mapCalendarEntries(entries: EventCalendarEntry[], view: 'day' | 'week' | 'month'): CalendarEvent[] {
    return entries.map(entry => {
      const startStr = entry.start_at ?? '';
      const endStr = entry.end_at ?? '';
      const start = view === 'month'
        ? toBerlinIso(DateTime.fromISO(startStr).startOf('day'))
        : toBerlinIso(startStr);
      const end = view === 'month'
        ? toBerlinIso(DateTime.fromISO(endStr).endOf('day'))
        : toBerlinIso(endStr);
      return {id: entry.id, resourceId: entry.row_id, entry, start, end};
    });
  }

  private mapOccurrences(occurrences: Occurrence[], view: 'day' | 'week' | 'month'): CalendarEvent[] {
    const defaultBorder = {color: 'transparent', width: '0px'};
    return occurrences.map(occ => {
      const fromBerlin = DateTime.fromISO(occ.from ?? '').setZone(BERLIN_TZ);
      const untilBerlin = DateTime.fromISO(occ.until ?? '').setZone(BERLIN_TZ);
      const start = toBerlinIso(view === 'month' ? fromBerlin.startOf('day') : fromBerlin);
      const end = toBerlinIso(view === 'month' ? untilBerlin.endOf('day') : untilBerlin);
      const resourceId = occ.category === OccurrenceCategoryEnum.PUBLIC_HOLIDAY
        ? OCCURRENCES_RESOURCE_ID
        : `${OCCURRENCE_CATEGORY_PREFIX}${occ.category}`;
      const entry: EventCalendarEntry = {
        id: occ.id,
        row_id: resourceId,
        event_id: null,
        parent_id: null,
        event_resource_id: null,
        blocked_resource_id: null,
        deleted_at: null,
        room_id: null,
        type: EventmanagementCalendarEntryTypeEnum.OCCURRENCE,
        occurrence: occ,
        start_at: toBerlinIso(fromBerlin) ?? null,
        end_at: toBerlinIso(untilBerlin) ?? null,
        styling: {
          font_color: '#ffffff',
          primary_background_color: '#6c757d',
          secondary_background_color: '#6c757d',
          background_type: CalendarEntryBackgroundStyleEnum.FILLED,
          top_border: defaultBorder,
          right_border: defaultBorder,
          bottom_border: defaultBorder,
          left_border: defaultBorder,
        },
      };
      return {id: `occurrence-${occ.id}`, resourceId, entry, start, end};
    });
  }
}
